import { Fragment } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ATSVacanciesModalTestIds } from 'data-testids/ATS';
import { getColumns, groupSchoolsByGroupId } from './utils';
import { Paper, Table, TableBody, TableContainer, useTheme } from '@mui/material';
import { useQueryGetActiveSchoolsByDistrict } from '../queries';
import { VacancyTableHeader } from './VacancyTableHeader';
import { VacancyTableRow } from './VacancyTableRow';
import auth from 'utils/auth';
import { VacancyGroupParentRow } from './VacancyGroupParentRow';
import { SchoolTableProps } from 'features/VacanciesModal/types';
import { ActiveDistrictSchool } from 'features/TalentMarketplace/TalentFiltersSidebar.types';
import { SCHOOLROLES_FORM_ID } from 'features/VacanciesModal/data';

export const notFoundIndex = -1;

export const SchoolTable: React.FC<SchoolTableProps> = ({ searchInput, variant }) => {
  const theme = useTheme();
  const districtId = auth.getUser()?.district?.id;
  const { data: schools } = useQueryGetActiveSchoolsByDistrict(districtId);
  const { control, getValues, watch } = useFormContext();
  const { append, remove } = useFieldArray({ control, name: SCHOOLROLES_FORM_ID });

  const filterSchoolsByName = (schoolsToFilter: ActiveDistrictSchool[]) => {
    if (!searchInput.trim()) return schoolsToFilter;
    const searchLower = searchInput.toLowerCase();
    return schoolsToFilter.filter((school) => school.name.toLowerCase().includes(searchLower));
  };

  const filteredSchools = schools ? filterSchoolsByName(schools) : null;
  const groupedFilteredSchools =
    filteredSchools && variant === 'group' && groupSchoolsByGroupId(filteredSchools);

  const selectAllOnClick = () => {
    if (!filteredSchools) return;
    const currentSchoolRoles = getValues(SCHOOLROLES_FORM_ID) || [];
    const allSelected = filteredSchools.every((school) =>
      currentSchoolRoles.some((role) => role.school_id === school.id)
    );

    if (allSelected) {
      const indicesToRemove = filteredSchools
        .map((school) => currentSchoolRoles.findIndex((role) => role.school_id === school.id))
        .filter((index) => index !== notFoundIndex)
        .sort((a, b) => b - a);
      indicesToRemove.forEach((index) => remove(index));
    } else {
      const schoolsToAdd = filteredSchools.filter(
        (school) => !currentSchoolRoles.some((role) => role.school_id === school.id)
      );
      schoolsToAdd.forEach((school) =>
        append({
          school_id: school.id,
          name: school.name,
          ftes_total: 1,
          visible_to_school_admin: true,
        })
      );
    }
  };

  const areAllChecked = () => {
    if (filteredSchools?.length === 0) return false;
    const schoolRoles = watch(SCHOOLROLES_FORM_ID) || [];

    return (
      filteredSchools?.every((school) => {
        return schoolRoles.some((item) => item?.school_id === school?.id);
      }) ?? false
    );
  };

  const areSomeChecked = () => {
    const schoolRoles = watch(SCHOOLROLES_FORM_ID) || [];

    return (
      filteredSchools?.some((school) => {
        return schoolRoles.some((item) => item?.school_id === school?.id);
      }) ?? false
    );
  };

  const columns = getColumns(theme, areAllChecked(), areSomeChecked(), selectAllOnClick);

  return (
    <TableContainer
      sx={{
        mt: 3,
        border: `1px solid ${theme.palette.border.main}`,
        borderRadius: theme.shape.borderRadius,
      }}
      component={Paper}
      data-testid={ATSVacanciesModalTestIds.GROUP_SCHOOL_TABLE}
    >
      <Table>
        <VacancyTableHeader columns={columns} />
        <TableBody>
          {variant === 'group' && (
            <>
              {groupedFilteredSchools?.map((group) => {
                return (
                  <Fragment key={group.groupId}>
                    <VacancyGroupParentRow group={group} numberOfColumns={columns.length} />
                    {group.schools.map((school) => {
                      return (
                        <VacancyTableRow
                          isChildRow={true}
                          fieldName={SCHOOLROLES_FORM_ID}
                          school={school}
                          key={school.id}
                        />
                      );
                    })}
                  </Fragment>
                );
              })}
            </>
          )}
          {variant === 'single' && (
            <>
              {filteredSchools?.map((school) => {
                return (
                  <VacancyTableRow
                    isChildRow={false}
                    fieldName={SCHOOLROLES_FORM_ID}
                    school={school}
                    key={school.id}
                  />
                );
              })}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
